import { MAIL_TEMPLATE_KEYS } from './mail-templates';

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function mailLayout(title: string, body: string, cta?: { label: string; href: string }) {
  const ctaHtml = cta
    ? `<p style="margin:24px 0;"><a href="${escapeHtml(cta.href)}" style="display:inline-block;padding:12px 20px;background:#111;color:#fff;text-decoration:none;border-radius:8px;font-weight:600;">${escapeHtml(cta.label)}</a></p>`
    : '';

  return `<!DOCTYPE html>
<html>
  <body style="margin:0;padding:24px;background:#f5f5f5;font-family:Inter,Segoe UI,sans-serif;color:#111;">
    <div style="max-width:560px;margin:0 auto;background:#fff;border:1px solid #e5e5e5;border-radius:12px;padding:28px;">
      <p style="margin:0 0 8px;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:#666;">Loamify</p>
      <h1 style="margin:0 0 16px;font-size:22px;line-height:1.3;">${escapeHtml(title)}</h1>
      <div style="font-size:15px;line-height:1.6;color:#333;">${body}</div>
      ${ctaHtml}
      <p style="margin:24px 0 0;font-size:12px;color:#888;">If you did not request this, you can ignore this email.</p>
    </div>
  </body>
</html>`;
}

export function renderMentionMissedEmail(params: { actorName: string; href: string }) {
  const subject = `${params.actorName} mentioned you on Loamify`;
  const text = `${params.actorName} mentioned you. View: ${params.href}`;
  const html = mailLayout(
    'You were mentioned',
    `<p><strong>${escapeHtml(params.actorName)}</strong> mentioned you while you were away.</p>`,
    { label: 'View mention', href: params.href },
  );
  return { subject, html, text, templateKey: MAIL_TEMPLATE_KEYS.NOTIFICATION_MENTION_MISSED };
}

export function renderDmMissedEmail(params: { senderName: string; preview: string; href: string }) {
  const subject = `New message from ${params.senderName}`;
  const text = `${params.senderName}: ${params.preview}\n${params.href}`;
  const html = mailLayout(
    'New direct message',
    `<p><strong>${escapeHtml(params.senderName)}</strong> sent you a message:</p><p style="padding:12px;background:#f7f7f7;border-radius:8px;">${escapeHtml(params.preview)}</p>`,
    { label: 'Open conversation', href: params.href },
  );
  return { subject, html, text, templateKey: MAIL_TEMPLATE_KEYS.NOTIFICATION_DM_MISSED };
}

export function renderDigestEmail(params: {
  lines: string[];
  frequency: 'daily' | 'weekly';
  notificationsHref: string;
}) {
  const isWeekly = params.frequency === 'weekly';
  const subject = isWeekly ? 'Your Loamify weekly digest' : 'Your Loamify daily digest';
  const title = isWeekly ? 'Your weekly digest' : 'Your daily digest';
  const text = params.lines.join('\n');
  const body = params.lines.map((line) => `<p style="margin:0 0 8px;">${escapeHtml(line)}</p>`).join('');
  const html = mailLayout(title, body, {
    label: 'Open notifications',
    href: params.notificationsHref,
  });
  const templateKey = isWeekly
    ? MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST
    : MAIL_TEMPLATE_KEYS.NOTIFICATION_DAILY_DIGEST;
  return { subject, html, text, templateKey };
}

export function renderImmediateNotificationEmail(params: { summary: string; href: string }) {
  const subject = params.summary;
  const text = `${params.summary}\n${params.href}`;
  const html = mailLayout(
    'New notification',
    `<p>${escapeHtml(params.summary)}</p>`,
    { label: 'View on Loamify', href: params.href },
  );
  return { subject, html, text, templateKey: MAIL_TEMPLATE_KEYS.NOTIFICATION_IMMEDIATE };
}

export function immediateNotificationSummary(
  type: string,
  actorName: string,
  payload: Record<string, unknown> = {},
): string {
  switch (type) {
    case 'post_liked':
      return `${actorName} liked your post`;
    case 'post_commented':
      return `${actorName} commented on your post`;
    case 'new_follower':
      return `${actorName} started following you`;
    case 'mention':
      return `${actorName} mentioned you`;
    case 'creator_post':
      return `${actorName} published a new post`;
    case 'post_reposted':
      return `${actorName} reposted your post`;
    case 'post_saved':
      return `${actorName} saved your post`;
    case 'direct_message':
      return `${actorName} sent you a message`;
    case 'creator_profile_completed':
      return 'Your creator profile is ready for preview';
    case 'moderation_action':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'A moderation action was taken on your account';
    case 'subscription_updated':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'Your subscription status changed';
    case 'community_join_welcome':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'Welcome to your new community';
    case 'community_reply':
      return `${actorName} replied in your community`;
    case 'community_mention':
      return `${actorName} mentioned you in a community`;
    case 'community_pin':
      return typeof payload.summary === 'string'
        ? payload.summary
        : `${actorName} pinned a community post`;
    case 'community_mod_action':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'A moderation action was taken in your community';
    case 'community_billing':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'Your community membership billing status changed';
    case 'community_application_reviewed':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'Your community application was reviewed';
    case 'course_drip_unlock':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'New course lessons are available';
    case 'course_certificate_ready':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'Your course certificate is ready';
    case 'course_new_lesson':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'A new course lesson is available';
    case 'course_qa_reply':
      return typeof payload.summary === 'string'
        ? payload.summary
        : `${actorName} replied to your course question`;
    case 'course_enrolled':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'You are enrolled in a course';
    case 'course_purchase_receipt':
      return typeof payload.summary === 'string'
        ? payload.summary
        : 'Your course purchase receipt';
    default:
      return `${actorName} interacted with you on Loamify`;
  }
}

export function pushNotificationContent(
  type: string,
  payload: Record<string, unknown>,
  actorName: string,
): { title: string; body: string; url: string } {
  const postId = typeof payload.postId === 'string' ? payload.postId : undefined;
  const conversationId =
    typeof payload.conversationId === 'string' ? payload.conversationId : undefined;
  const username =
    (typeof payload.username === 'string' && payload.username) ||
    (typeof payload.actorUsername === 'string' && payload.actorUsername) ||
    undefined;

  let url = '/notifications';
  switch (type) {
    case 'direct_message':
      url = conversationId ? `/messages/${conversationId}` : '/messages';
      break;
    case 'new_follower':
      url = username ? `/u/${username}` : '/notifications';
      break;
    case 'creator_profile_completed':
      url = username ? `/u/${username}` : '/become-creator';
      break;
    case 'moderation_action':
      url = typeof payload.href === 'string' ? payload.href : '/settings/account';
      break;
    case 'subscription_updated':
      url = '/settings/account/creator-plan';
      break;
    case 'community_join_welcome':
    case 'community_reply':
    case 'community_mention':
    case 'community_pin':
    case 'community_mod_action':
    case 'community_billing': {
      const slug = typeof payload.communitySlug === 'string' ? payload.communitySlug : undefined;
      url = slug ? `/circles/${slug}` : '/notifications';
      break;
    }
    case 'course_drip_unlock': {
      const href = typeof payload.href === 'string' ? payload.href : undefined;
      url = href ?? (typeof payload.courseSlug === 'string'
        ? `/courses/${payload.courseSlug}/learn`
        : '/learning');
      break;
    }
    case 'course_certificate_ready': {
      const href = typeof payload.href === 'string' ? payload.href : undefined;
      url = href ?? (typeof payload.courseSlug === 'string'
        ? `/courses/${payload.courseSlug}/learn?celebrate=1`
        : '/learning');
      break;
    }
    case 'course_new_lesson':
    case 'course_qa_reply':
    case 'course_enrolled': {
      const href = typeof payload.href === 'string' ? payload.href : undefined;
      const lessonId = typeof payload.lessonId === 'string' ? payload.lessonId : undefined;
      const courseSlug = typeof payload.courseSlug === 'string' ? payload.courseSlug : undefined;
      if (href) {
        url = href;
      } else if (courseSlug) {
        url = lessonId
          ? `/courses/${courseSlug}/learn?lesson=${lessonId}`
          : `/courses/${courseSlug}/learn`;
      } else {
        url = '/learning';
      }
      break;
    }
    case 'course_purchase_receipt': {
      const receiptUrl = typeof payload.receiptUrl === 'string' ? payload.receiptUrl : undefined;
      url = receiptUrl ?? '/learning';
      break;
    }
    default:
      url = postId ? `/posts/${postId}` : '/notifications';
  }

  const preview =
    typeof payload.preview === 'string' ? payload.preview.slice(0, 120) : undefined;

  return {
    title: actorName,
    body: preview ?? immediateNotificationSummary(type, actorName, payload),
    url,
  };
}
